import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TOUR_STOPS,
  STOP_DURATION,
  FLIGHT_DURATION,
} from '../../data/metamedia-tour';
import '../styles/map-tour.css';

const ACCENT = '#FFBB56';

// Renderer interaction handlers switched off so the view is tour-driven only
const HANDLERS = [
  'dragPan',
  'dragRotate',
  'scrollZoom',
  'boxZoom',
  'doubleClickZoom',
  'touchZoomRotate',
  'touchPitch',
  'keyboard',
];

// Only stops that declare a `pinLabel` get a marker — the office is the single
// place worth pinning, the other stops are camera moves around it
const PINS = TOUR_STOPS.filter(stop => stop.pinLabel).map(stop => ({
  location: stop.location,
  label: stop.pinLabel,
  stopIndex: TOUR_STOPS.indexOf(stop),
}));

// Longdo HTML marker: the outermost element must have no `position`, and is
// centred with negative margins (not `offset`) or the pin drifts while zooming
const pinHtml = (index, pin) => {
  const size = 44;
  return (
    `<div style="width:${size}px;height:${size}px;` +
    `margin-left:-${size / 2}px;margin-top:-${size / 2}px">` +
    `<div id="tour-pin-${index}" class="tour-pin">` +
    `<span class="tour-pin__wave"></span>` +
    `<span class="tour-pin__dot"></span>` +
    `<span class="tour-pin__label">${pin.label}</span>` +
    `</div>` +
    `</div>`
  );
};

export default function MetamediaMapTour() {
  const mapRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isReady, setIsReady] = useState(false);

  const stop = TOUR_STOPS[activeIndex];

  // Fly the camera to a stop
  const flyTo = useCallback(index => {
    const map = mapRef.current;
    if (!map) return;
    const target = TOUR_STOPS[index];

    // A stop can either fit a bounding box or fly to a fixed zoom level
    if (target.bound) {
      // Pad around the HUD card so the fitted area is not hidden behind it
      const narrow = window.innerWidth < 620;
      map.bound(target.bound, {
        padding: narrow
          ? { top: 250, right: 24, bottom: 80, left: 24 }
          : { top: 40, right: 56, bottom: 80, left: 380 },
        duration: FLIGHT_DURATION,
      });
      return;
    }

    // One combined camera move — calling location() and zoom() separately makes
    // the second animation cancel the first, so the map never leaves the city
    map.goTo(
      {
        location: target.location,
        zoom: target.zoom,
        duration: FLIGHT_DURATION,
      },
      true
    );
  }, []);

  // Province boundaries stay on the map for the whole tour: geometry loaded via
  // Overlays.load() cannot be taken off again with Overlays.remove()
  const loadBoundaries = useCallback(map => {
    TOUR_STOPS.filter(item => item.province).forEach(item => {
      map.Overlays.load(
        new window.longdo.Overlays.Object(item.province.code, 'IG', {
          simplify: '0.0008',
          lineWidth: 2,
          lineColor: ACCENT,
          fillColor: 'rgba(255, 187, 86, 0.14)',
          clickable: false,
          weight: 1,
          title: item.province.title,
          detail: '',
          label: '',
          pointer: false,
        })
      );
    });
  }, []);

  // Create the map once, when the component mounts (i.e. the modal opens)
  useEffect(() => {
    let timer;
    const init = () => {
      const placeholder = document.getElementById('metamedia-map');
      if (!window.longdo || !placeholder) return;

      const map = new window.longdo.Map({
        placeholder,
        src: 'https://api.longdo.com/map3/',
        layer: [window.longdo.Layers.NIGHT],
        ui: window.longdo.UiComponent.None,
        lastView: false,
      });
      mapRef.current = map;

      map.Event.bind('ready', () => {
        // Keep the basemap quiet so pins and the boundary stand out
        try {
          map.Renderer.setLayoutProperty('building', 'visibility', 'none');
        } catch (e) {
          // Layer names may change upstream — never break the tour over it
        }

        // The map is a narrated view: it only moves with the tour. Note that
        // the `input: false` map option cannot do this (the API reads it as
        // `input || true`), so the renderer handlers are disabled directly.
        // Wheel events then fall through to the page instead of zooming.
        HANDLERS.forEach(name => {
          try {
            map.Renderer?.[name]?.disable?.();
          } catch (e) {
            // Renderer internals may change — a draggable map is acceptable
          }
        });

        PINS.forEach((pin, index) => {
          const marker = new window.longdo.Marker(pin.location, {
            icon: { html: pinHtml(index, pin), offset: { x: 0, y: 0 } },
            title: pin.label,
          });
          // Clicking a pin jumps to the stop it belongs to
          marker._stopIndex = pin.stopIndex;
          map.Overlays.add(marker);
        });

        map.Event.bind('overlayClick', overlay => {
          const index = overlay?._stopIndex;
          if (typeof index !== 'number' || index < 0) return;
          setIsPlaying(false);
          setActiveIndex(index);
        });

        loadBoundaries(map);
        setIsReady(true);
        flyTo(0);
      });
    };

    // Wait for the modal to size the placeholder before creating the map
    timer = setTimeout(init, 80);
    return () => clearTimeout(timer);
  }, [flyTo, loadBoundaries]);

  useEffect(() => {
    if (isReady) flyTo(activeIndex);
  }, [activeIndex, isReady, flyTo]);

  // The office pin stays lit through the whole tour; only its accent changes on
  // the milestone stop. Done via the DOM so markers are never recreated.
  useEffect(() => {
    if (!isReady) return;
    PINS.forEach((_, index) => {
      const el = document.getElementById(`tour-pin-${index}`);
      if (!el) return;
      el.classList.add('is-active');
      el.classList.toggle(
        'tour-pin--milestone',
        Boolean(TOUR_STOPS[activeIndex].milestone)
      );
    });
  }, [activeIndex, isReady]);

  useEffect(() => {
    if (!isPlaying || !isReady) return;
    const timer = setTimeout(
      () => setActiveIndex(i => (i + 1) % TOUR_STOPS.length),
      STOP_DURATION
    );
    return () => clearTimeout(timer);
  }, [activeIndex, isPlaying, isReady]);

  const go = delta => {
    setIsPlaying(false);
    setActiveIndex(i => (i + delta + TOUR_STOPS.length) % TOUR_STOPS.length);
  };

  return (
    <div className="map-tour">
      <div id="metamedia-map" className="map-tour__canvas" />

      <div className="map-tour__frame" aria-hidden="true" />

      <div className="map-tour__coords">
        {stop.bound
          ? `${stop.bound.minLat}–${stop.bound.maxLat}°N ` +
            `${stop.bound.minLon}–${stop.bound.maxLon}°E`
          : `${stop.location.lat.toFixed(4)}°N ` +
            `${stop.location.lon.toFixed(4)}°E · Z${stop.zoom}`}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={stop.id}
          className={`map-tour__card ${stop.milestone ? 'is-milestone' : ''}`}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
        >
          <div className="map-tour__eyebrow">
            <span className="map-tour__year">
              {stop.milestone && <span className="map-tour__star">★</span>}
              {stop.label}
            </span>
            <span className="map-tour__count">
              {String(activeIndex + 1).padStart(2, '0')} /{' '}
              {String(TOUR_STOPS.length).padStart(2, '0')}
            </span>
          </div>
          <h4 className="map-tour__title">{stop.title}</h4>
          <p className="map-tour__place">{stop.place}</p>
          <p className="map-tour__summary">{stop.summary}</p>
          <div className="map-tour__stack">
            {stop.stack.map(tech => (
              <span className="map-tour__chip" key={tech}>
                {tech}
              </span>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="map-tour__controls">
        <button
          type="button"
          className="map-tour__btn"
          onClick={() => go(-1)}
          aria-label="Previous stop"
        >
          ‹
        </button>
        <button
          type="button"
          className="map-tour__btn map-tour__btn--play"
          onClick={() => setIsPlaying(p => !p)}
          aria-label={isPlaying ? 'Pause tour' : 'Play tour'}
        >
          {isPlaying ? '❙❙' : '▶'}
        </button>
        <button
          type="button"
          className="map-tour__btn"
          onClick={() => go(1)}
          aria-label="Next stop"
        >
          ›
        </button>

        <div className="map-tour__dots">
          {TOUR_STOPS.map((item, index) => (
            <button
              type="button"
              key={item.id}
              className={`map-tour__dot ${
                index === activeIndex ? 'is-active' : ''
              }`}
              onClick={() => {
                setIsPlaying(false);
                setActiveIndex(index);
              }}
              aria-label={item.title}
            >
              {index === activeIndex && isPlaying && (
                <span
                  className="map-tour__dot-fill"
                  style={{ animationDuration: `${STOP_DURATION}ms` }}
                />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
